import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { MessageCircle, Send, History } from "lucide-react";

export const REMINDER_TEMPLATES = [
  {
    name: "Gentle reminder",
    text:
      "Hi {name}, gentle reminder from Vasavi Boys Hostel — your dues of ₹{dues} for room {room} are pending. Please pay at your earliest convenience. Thank you!",
  },
  {
    name: "Payment due",
    text:
      "Dear {name}, this is to remind you that ₹{dues} is due for your stay at Vasavi Boys Hostel (Room {room}). Kindly clear the dues to avoid late charges.",
  },
  {
    name: "Final reminder",
    text:
      "Hi {name}, we've already sent multiple reminders regarding pending dues of ₹{dues} for room {room}. Please clear them urgently to avoid further action. — Warden, Vasavi Boys Hostel",
  },
];

function formatMessage(template, student) {
  const dues = Math.max(
    0,
    Number(student.total_fees || 0) -
      Number(student.fees_paid || 0) -
      Number(student.discount_amount || 0),
  );
  return template
    .replaceAll("{name}", student.full_name || "resident")
    .replaceAll("{dues}", dues.toLocaleString("en-IN"))
    .replaceAll("{room}", student.flat_number || "-")
    .replaceAll("{total}", Number(student.total_fees || 0).toLocaleString("en-IN"))
    .replaceAll("{paid}", Number(student.fees_paid || 0).toLocaleString("en-IN"));
}

export default function ReminderDialog({ open, onOpenChange, student, onSent }) {
  const [templateIdx, setTemplateIdx] = useState(0);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !student) return;
    setTemplateIdx(0);
    setMessage(formatMessage(REMINDER_TEMPLATES[0].text, student));
    api
      .get("/reminders", { params: { student_id: student.id } })
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]));
  }, [open, student]);

  const pickTemplate = (idx) => {
    setTemplateIdx(idx);
    setMessage(formatMessage(REMINDER_TEMPLATES[idx].text, student));
  };

  const contact = useMemo(() => {
    const parts = [];
    if (student?.contact_number) parts.push(`Student: ${student.contact_number}`);
    if (student?.parent_contact_number) parts.push(`Parent: ${student.parent_contact_number}`);
    return parts.join(" · ") || "no contact on file";
  }, [student]);

  const send = async () => {
    if (!message.trim()) {
      toast.error("Message can't be empty");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/reminders", {
        student_id: student.id,
        message,
        channel: "whatsapp",
      });
      // Open WhatsApp for BOTH student's own number AND parent's number.
      // Fall back to legacy single wa_link when a mixed-version backend returns only that.
      let links = [];
      if (Array.isArray(data.wa_links) && data.wa_links.length) {
        links = data.wa_links.filter((l) => l && l.link);
      } else if (data.wa_link) {
        links = [{ label: "Contact", link: data.wa_link }];
      }
      if (links.length === 0) {
        toast.warning("No contact numbers on file — reminder logged but not sent");
      } else {
        links.forEach((l, idx) => {
          setTimeout(() => window.open(l.link, "_blank", "noopener,noreferrer"), idx * 150);
        });
        toast.success(
          `Reminder logged — WhatsApp opened for ${links.map((l) => l.label).join(" + ")}`,
        );
      }
      onSent?.();
      setHistory((h) => [data, ...h]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto border-2 border-[#0A0A0A] shadow-brutal rounded-xl bg-white"
        data-testid="reminder-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2.5 border-2 border-[#0A0A0A] rounded-xl bg-[#00CC66]">
              <MessageCircle size={22} strokeWidth={2.5} className="text-white" />
            </div>
            <div>
              <DialogTitle className="font-heading font-black text-2xl tracking-tighter">
                Send WhatsApp Reminder
              </DialogTitle>
              <DialogDescription className="text-sm font-medium text-[#4A4A4A]">
                To <span className="font-bold">{student?.full_name}</span> · {contact}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Template picker */}
        <div className="mt-2">
          <div className="text-xs font-bold uppercase tracking-widest mb-2">Template</div>
          <div className="flex flex-wrap gap-2">
            {REMINDER_TEMPLATES.map((t, i) => (
              <button
                key={t.name}
                onClick={() => pickTemplate(i)}
                data-testid={`reminder-template-${i}`}
                className={`px-3 py-1.5 text-xs font-bold border-2 border-[#0A0A0A] rounded-full transition-transform ${
                  templateIdx === i ? "bg-[#0A0A0A] text-[#FFE600]" : "bg-white"
                }`}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-widest mb-2">Message</div>
          <textarea
            data-testid="reminder-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white font-medium"
          />
          <p className="text-[11px] font-medium text-[#4A4A4A] mt-1.5">
            Placeholders: {"{name} {dues} {room} {total} {paid}"} — replaced live.
          </p>
        </div>

        {history.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2">
              <History size={14} strokeWidth={2.5} />
              <span className="text-xs font-bold uppercase tracking-widest">
                Sent history ({history.length})
              </span>
            </div>
            <div className="max-h-40 overflow-y-auto space-y-2 border-2 border-[#0A0A0A] rounded-xl p-3 bg-[#FFFCE0]">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="text-xs font-medium border-b border-[#0A0A0A]/20 pb-2 last:border-b-0"
                  data-testid={`reminder-history-${h.id}`}
                >
                  <div className="font-bold">
                    {new Date(h.sent_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    · by {h.sent_by}
                  </div>
                  <div className="mt-0.5 line-clamp-2 text-[#4A4A4A]">{h.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4 gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-white"
            data-testid="reminder-cancel"
          >
            Close
          </button>
          <button
            onClick={send}
            disabled={sending}
            data-testid="reminder-send"
            className="inline-flex items-center gap-2 px-5 py-2.5 font-bold border-2 border-[#0A0A0A] rounded-full bg-[#00CC66] text-white shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
          >
            <Send size={16} strokeWidth={2.5} />
            {sending ? "Sending..." : "Open WhatsApp & log"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
